import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Typography,
  IconButton,
  Chip,
  Grid,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Person as PersonIcon,
  Computer as ComputerIcon,
  Apps as AppsIcon,
  Memory as MemoryIcon,
  InfoOutlined as InfoIcon
} from '@mui/icons-material';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';

const DeviceSoftwareTab = ({ deviceId }) => {
  const { user } = useAuth();
  const [softwareList, setSoftwareList] = useState([]);
  const [catalogSoftware, setCatalogSoftware] = useState([]);
  const [selectedSoftwareVersions, setSelectedSoftwareVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [selectedDeviceSoftware, setSelectedDeviceSoftware] = useState(null);
  const [showPasswords, setShowPasswords] = useState({});
  const [editingAccount, setEditingAccount] = useState(null);

  const [softwareFormData, setSoftwareFormData] = useState({
    software_id: '',
    version_id: null,
    custom_version: '',
    installation_date: '',
    license_key: '',
    notes: ''
  });

  const [accountFormData, setAccountFormData] = useState({
    account_type: 'administrator',
    username: '',
    password: '',
    description: '',
    device_user_id: null,
    is_active: true
  });

  const [deviceUsers, setDeviceUsers] = useState([]);

  // app services (ports) + flows (app→app)
  const [services, setServices] = useState([]);       // services on this device
  const [allServices, setAllServices] = useState([]); // all services (flow destination picker)
  const [flows, setFlows] = useState([]);             // flows touching this device
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [flowDialogOpen, setFlowDialogOpen] = useState(false);
  const [activeSw, setActiveSw] = useState(null);     // device_software the dialog acts on
  const [serviceForm, setServiceForm] = useState({ name: '', port: '', l4_protocol: 'tcp', app_protocol: '', is_active: true });
  const [flowForm, setFlowForm] = useState({ name: '', dest_service_id: null, dest_host: '', dest_port: '', l4_protocol: 'tcp', app_protocol: '', purpose: '', freeDest: false });

  useEffect(() => {
    fetchDeviceSoftware();
    fetchCatalogSoftware();
    fetchDeviceUsers();
    fetchServices();
    fetchFlows();
    fetchAllServices();
  }, [deviceId]);

  const fetchServices = async () => {
    try { const r = await axios.get('/app-services', { params: { device_id: deviceId } }); setServices(r.data || []); }
    catch (err) { console.error('services', err); }
  };
  const fetchFlows = async () => {
    try { const r = await axios.get('/app-flows', { params: { device_id: deviceId } }); setFlows(r.data || []); }
    catch (err) { console.error('flows', err); }
  };
  const fetchAllServices = async () => {
    try { const r = await axios.get('/app-services', { params: { limit: 1000 } }); setAllServices(r.data || []); }
    catch (err) { console.error('allServices', err); }
  };

  const openServiceDialog = (sw) => {
    setActiveSw(sw);
    setServiceForm({ name: '', port: '', l4_protocol: 'tcp', app_protocol: '', is_active: true });
    setServiceDialogOpen(true);
  };
  const handleAddService = async () => {
    try {
      await axios.post('/app-services', {
        device_software_id: activeSw.id, name: serviceForm.name,
        port: serviceForm.port === '' ? null : Number(serviceForm.port),
        l4_protocol: serviceForm.l4_protocol, app_protocol: serviceForm.app_protocol || null,
        is_active: serviceForm.is_active,
      });
      setServiceDialogOpen(false);
      fetchServices(); fetchAllServices();
    } catch (err) { setError(err.response?.data?.detail || 'Ошибка добавления сервиса'); }
  };
  const handleDeleteService = async (id) => {
    if (!window.confirm('Удалить сервис?')) return;
    try { await axios.delete(`/app-services/${id}`); fetchServices(); fetchAllServices(); fetchFlows(); }
    catch (err) { setError('Ошибка удаления сервиса'); }
  };

  const openFlowDialog = (sw) => {
    setActiveSw(sw);
    setFlowForm({ name: '', dest_service_id: null, dest_host: '', dest_port: '', l4_protocol: 'tcp', app_protocol: '', purpose: '', freeDest: false });
    setFlowDialogOpen(true);
  };
  const handleAddFlow = async () => {
    try {
      const body = {
        source_device_software_id: activeSw.id, name: flowForm.name || null,
        l4_protocol: flowForm.l4_protocol, app_protocol: flowForm.app_protocol || null,
        purpose: flowForm.purpose || null,
      };
      if (flowForm.freeDest) {
        body.dest_host = flowForm.dest_host || null;
        body.dest_port = flowForm.dest_port === '' ? null : Number(flowForm.dest_port);
      } else {
        body.dest_service_id = flowForm.dest_service_id;
      }
      await axios.post('/app-flows', body);
      setFlowDialogOpen(false);
      fetchFlows();
    } catch (err) { setError(err.response?.data?.detail || 'Ошибка добавления потока'); }
  };
  const handleDeleteFlow = async (id) => {
    if (!window.confirm('Удалить поток?')) return;
    try { await axios.delete(`/app-flows/${id}`); fetchFlows(); }
    catch (err) { setError('Ошибка удаления потока'); }
  };

  const fetchDeviceSoftware = async () => {
    try {
      const response = await axios.get(`/software/devices/${deviceId}/software`);
      setSoftwareList(response.data);
    } catch (err) {
      console.error('Error fetching device software:', err);
    }
  };

  const fetchCatalogSoftware = async () => {
    try {
      const response = await axios.get('/software/catalog', { params: { limit: 1000 } });
      setCatalogSoftware(response.data);
    } catch (err) {
      console.error('Error fetching catalog:', err);
    }
  };

  const fetchDeviceUsers = async () => {
    try {
      const response = await axios.get('/device-users/', { params: { limit: 1000, is_active: true } });
      setDeviceUsers(response.data);
    } catch (err) {
      console.error('Error fetching device users:', err);
    }
  };

  const fetchSoftwareVersions = async (softwareId) => {
    try {
      const response = await axios.get(`/software/catalog/${softwareId}`);
      setSelectedSoftwareVersions(response.data.versions || []);
    } catch (err) {
      console.error('Error fetching versions:', err);
      setSelectedSoftwareVersions([]);
    }
  };

  const handleAddSoftware = async () => {
    try {
      await axios.post(`/software/devices/${deviceId}/software`, softwareFormData);
      setAddDialogOpen(false);
      fetchDeviceSoftware();
      setSoftwareFormData({
        software_id: '',
        version_id: null,
        custom_version: '',
        installation_date: '',
        license_key: '',
        notes: ''
      });
    } catch (err) {
      setError('Ошибка добавления ПО');
      console.error(err);
    }
  };

  const handleRemoveSoftware = async (softwareId) => {
    if (!window.confirm('Удалить это ПО с устройства?')) return;

    try {
      await axios.delete(`/software/device-software/${softwareId}`);
      fetchDeviceSoftware();
    } catch (err) {
      setError('Ошибка удаления ПО');
    }
  };

  const handleOpenAccountDialog = (deviceSoftware, account = null) => {
    setSelectedDeviceSoftware(deviceSoftware);
    if (account) {
      // Editing mode
      setEditingAccount(account);
      setAccountFormData({
        account_type: account.account_type,
        username: account.username,
        password: account.password || '',
        description: account.description || '',
        device_user_id: account.device_user?.id || null,
        is_active: account.is_active
      });
    } else {
      // Adding mode
      setEditingAccount(null);
      setAccountFormData({
        account_type: 'administrator',
        username: '',
        password: '',
        description: '',
        device_user_id: null,
        is_active: true
      });
    }
    setAccountDialogOpen(true);
  };

  const handleAddAccount = async () => {
    try {
      if (editingAccount) {
        // Update existing account
        await axios.put(`/software/accounts/${editingAccount.id}`, accountFormData);
      } else {
        // Create new account
        await axios.post(`/software/device-software/${selectedDeviceSoftware.id}/accounts`, accountFormData);
      }
      setAccountDialogOpen(false);
      fetchDeviceSoftware();
      setEditingAccount(null);
      setAccountFormData({
        account_type: 'administrator',
        username: '',
        password: '',
        description: '',
        device_user_id: null,
        is_active: true
      });
    } catch (err) {
      setError(editingAccount ? 'Ошибка обновления учетной записи' : 'Ошибка добавления учетной записи');
      console.error(err);
    }
  };

  const handleDeleteAccount = async (accountId) => {
    if (!window.confirm('Удалить эту учетную запись?')) return;

    try {
      await axios.delete(`/software/accounts/${accountId}`);
      fetchDeviceSoftware();
    } catch (err) {
      setError('Ошибка удаления учетной записи');
    }
  };

  const togglePasswordVisibility = (accountId) => {
    setShowPasswords(prev => ({ ...prev, [accountId]: !prev[accountId] }));
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'operating_system':
        return <ComputerIcon />;
      case 'application':
        return <AppsIcon />;
      case 'firmware':
        return <MemoryIcon />;
      default:
        return <ComputerIcon />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'operating_system':
        return 'ОС';
      case 'application':
        return 'Приложение';
      case 'firmware':
        return 'Прошивка';
      default:
        return type;
    }
  };

  const getAccountTypeLabel = (type) => {
    switch (type) {
      case 'administrator':
        return 'Администратор';
      case 'user':
        return 'Пользователь';
      case 'service':
        return 'Сервисная';
      case 'guest':
        return 'Гостевая';
      default:
        return type;
    }
  };

  const getAccountTypeColor = (type) => {
    switch (type) {
      case 'administrator':
        return 'error';
      case 'user':
        return 'success';
      case 'service':
        return 'warning';
      case 'guest':
        return 'default';
      default:
        return 'default';
    }
  };

  const canManage = user?.is_superuser || user?.permissions?.includes('software_accounts.manage');
  const canViewPasswords = user?.is_superuser || user?.permissions?.includes('software_passwords.view');

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
        {canManage && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            Добавить ПО
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {softwareList.length === 0 ? (
        <Alert severity="info">На этом устройстве не установлено ПО</Alert>
      ) : (
        <Grid container spacing={2}>
          {softwareList.map((sw) => (
            <Grid item xs={12} key={sw.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      {getTypeIcon(sw.software?.type)}
                      <Box>
                        <Typography variant="h6">{sw.software?.name}</Typography>
                        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                          <Chip
                            label={getTypeLabel(sw.software?.type)}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          <Chip
                            label={`Версия: ${sw.custom_version || sw.version?.version || 'Не указана'}`}
                            size="small"
                          />
                        </Box>
                      </Box>
                    </Box>
                    {canManage && (
                      <IconButton onClick={() => handleRemoveSoftware(sw.id)} color="error" size="small">
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>

                  {sw.notes && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {sw.notes}
                    </Typography>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">
                      Учетные записи ({sw.accounts?.length || 0})
                    </Typography>
                    {canManage && (
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenAccountDialog(sw)}
                      >
                        Добавить учетку
                      </Button>
                    )}
                  </Box>

                  {sw.accounts && sw.accounts.length > 0 ? (
                    <List dense>
                      {sw.accounts.map((account) => (
                        <ListItem
                          key={account.id}
                          secondaryAction={
                            canManage && (
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenAccountDialog(sw, account)}
                                  color="primary"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  edge="end"
                                  onClick={() => handleDeleteAccount(account.id)}
                                  size="small"
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            )
                          }
                          sx={{ bgcolor: 'background.default', mb: 1, borderRadius: 1 }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                <PersonIcon fontSize="small" />
                                <Typography variant="body2" fontWeight="500">{account.username}</Typography>
                                <Chip
                                  label={getAccountTypeLabel(account.account_type)}
                                  size="small"
                                  color={getAccountTypeColor(account.account_type)}
                                />
                                {!account.is_active && (
                                  <Chip label="Неактивна" size="small" color="default" />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 0.5 }}>
                                {account.device_user && (
                                  <Typography variant="caption" display="block" color="primary" sx={{ mb: 0.5 }}>
                                    👤 Пользователь: {account.device_user.full_name}
                                    {account.device_user.department && ` (${account.device_user.department})`}
                                    {account.device_user.position && ` - ${account.device_user.position}`}
                                  </Typography>
                                )}
                                {account.description && (
                                  <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                    📝 {account.description}
                                  </Typography>
                                )}
                                {canViewPasswords && account.password && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                    <Typography variant="caption" component="span" sx={{ fontFamily: 'monospace' }}>
                                      🔑 Пароль: {showPasswords[account.id] ? account.password : '••••••••'}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => togglePasswordVisibility(account.id)}
                                      sx={{ ml: 0.5 }}
                                    >
                                      {showPasswords[account.id] ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                    </IconButton>
                                  </Box>
                                )}
                                {account.last_password_change && (
                                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                    🕒 Последнее изменение пароля: {new Date(account.last_password_change).toLocaleDateString()}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Нет учетных записей
                    </Typography>
                  )}

                  {/* ── Services / ports ── */}
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">
                      Сервисы / порты ({services.filter((s) => s.device_software_id === sw.id).length})
                    </Typography>
                    {canManage && (
                      <Button size="small" startIcon={<AddIcon />} onClick={() => openServiceDialog(sw)}>
                        Добавить сервис
                      </Button>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {services.filter((s) => s.device_software_id === sw.id).map((s) => (
                      <Chip
                        key={s.id}
                        label={`${s.name} :${s.port ?? '-'}/${s.l4_protocol}${s.app_protocol ? ` (${s.app_protocol})` : ''}`}
                        size="small"
                        variant="outlined"
                        onDelete={canManage ? () => handleDeleteService(s.id) : undefined}
                      />
                    ))}
                    {services.filter((s) => s.device_software_id === sw.id).length === 0 && (
                      <Typography variant="caption" color="text.secondary">Нет сервисов</Typography>
                    )}
                  </Box>

                  {/* ── Outbound flows ── */}
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2">
                      Исходящие потоки ({flows.filter((f) => f.source_device_software_id === sw.id).length})
                    </Typography>
                    {canManage && (
                      <Button size="small" startIcon={<AddIcon />} onClick={() => openFlowDialog(sw)}>
                        Добавить поток
                      </Button>
                    )}
                  </Box>
                  {flows.filter((f) => f.source_device_software_id === sw.id).length > 0 ? (
                    <List dense>
                      {flows.filter((f) => f.source_device_software_id === sw.id).map((f) => (
                        <ListItem
                          key={f.id}
                          secondaryAction={canManage && (
                            <IconButton size="small" color="error" onClick={() => handleDeleteFlow(f.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                          sx={{ bgcolor: 'background.default', mb: 1, borderRadius: 1 }}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="body2">
                                → {f.dest_service_label || f.dest_device_name || f.dest_host || '?'}
                                {f.dest_port ? ` :${f.dest_port}` : ''}/{f.l4_protocol}
                              </Typography>
                            }
                            secondary={[f.app_protocol, f.purpose, f.name].filter(Boolean).join(' · ') || null}
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="caption" color="text.secondary">Нет потоков</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add Software Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Добавить ПО к устройству</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              options={catalogSoftware}
              getOptionLabel={(option) => `${option.name} (${option.vendor || 'Без производителя'})`}
              value={catalogSoftware.find(s => s.id === softwareFormData.software_id) || null}
              onChange={(e, newValue) => {
                setSoftwareFormData({ ...softwareFormData, software_id: newValue?.id || '' });
                if (newValue) {
                  fetchSoftwareVersions(newValue.id);
                } else {
                  setSelectedSoftwareVersions([]);
                }
              }}
              renderInput={(params) => <TextField {...params} label="Программное обеспечение" required />}
            />

            {selectedSoftwareVersions.length > 0 && (
              <TextField
                label="Версия"
                fullWidth
                select
                value={softwareFormData.version_id || ''}
                onChange={(e) => setSoftwareFormData({ ...softwareFormData, version_id: e.target.value || null })}
              >
                <MenuItem value="">Другая версия</MenuItem>
                {selectedSoftwareVersions.map((v) => (
                  <MenuItem key={v.id} value={v.id}>
                    {v.version}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <TextField
              label="Или введите версию вручную"
              fullWidth
              value={softwareFormData.custom_version}
              onChange={(e) => setSoftwareFormData({ ...softwareFormData, custom_version: e.target.value })}
            />

            <TextField
              label="Дата установки"
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
              value={softwareFormData.installation_date}
              onChange={(e) => setSoftwareFormData({ ...softwareFormData, installation_date: e.target.value })}
            />

            <TextField
              label="Лицензионный ключ"
              fullWidth
              value={softwareFormData.license_key}
              onChange={(e) => setSoftwareFormData({ ...softwareFormData, license_key: e.target.value })}
            />

            <TextField
              label="Заметки"
              fullWidth
              multiline
              rows={2}
              value={softwareFormData.notes}
              onChange={(e) => setSoftwareFormData({ ...softwareFormData, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleAddSoftware} variant="contained" disabled={!softwareFormData.software_id}>
            Добавить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Account Dialog */}
      <Dialog open={accountDialogOpen} onClose={() => setAccountDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingAccount ? 'Редактировать учетную запись' : 'Новая учетная запись'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Тип учетной записи"
              fullWidth
              required
              select
              value={accountFormData.account_type}
              onChange={(e) => setAccountFormData({ ...accountFormData, account_type: e.target.value })}
            >
              <MenuItem value="administrator">Администратор</MenuItem>
              <MenuItem value="user">Пользователь</MenuItem>
              <MenuItem value="service">Сервисная</MenuItem>
              <MenuItem value="guest">Гостевая</MenuItem>
            </TextField>

            <TextField
              label="Имя пользователя"
              fullWidth
              required
              value={accountFormData.username}
              onChange={(e) => setAccountFormData({ ...accountFormData, username: e.target.value })}
            />

            <TextField
              label="Пароль"
              fullWidth
              type="password"
              value={accountFormData.password}
              onChange={(e) => setAccountFormData({ ...accountFormData, password: e.target.value })}
            />

            <Autocomplete
              options={deviceUsers}
              getOptionLabel={(option) => `${option.full_name}${option.department ? ` (${option.department})` : ''}`}
              value={deviceUsers.find(u => u.id === accountFormData.device_user_id) || null}
              onChange={(e, newValue) => {
                setAccountFormData({ ...accountFormData, device_user_id: newValue?.id || null });
              }}
              renderInput={(params) => (
                <TextField {...params} label="Пользователь устройства (опционально)" />
              )}
            />

            <TextField
              label="Описание"
              fullWidth
              value={accountFormData.description}
              onChange={(e) => setAccountFormData({ ...accountFormData, description: e.target.value })}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={accountFormData.is_active}
                  onChange={(e) => setAccountFormData({ ...accountFormData, is_active: e.target.checked })}
                />
              }
              label="Активна"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccountDialogOpen(false)}>Отмена</Button>
          <Button
            onClick={handleAddAccount}
            variant="contained"
            disabled={!accountFormData.username}
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Service dialog */}
      <Dialog open={serviceDialogOpen} onClose={() => setServiceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Сервис (порт приложения){activeSw ? ` — ${activeSw.software?.name || ''}` : ''}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Название (напр. HTTPS UI, Modbus/TCP)" required
              value={serviceForm.name} onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Порт" type="number" sx={{ flex: 1 }}
                value={serviceForm.port} onChange={(e) => setServiceForm({ ...serviceForm, port: e.target.value })} />
              <TextField label="L4" select sx={{ flex: 1 }}
                value={serviceForm.l4_protocol} onChange={(e) => setServiceForm({ ...serviceForm, l4_protocol: e.target.value })}>
                <MenuItem value="tcp">TCP</MenuItem>
                <MenuItem value="udp">UDP</MenuItem>
                <MenuItem value="icmp">ICMP</MenuItem>
                <MenuItem value="other">Другой</MenuItem>
              </TextField>
              <TextField label="Протокол (http/modbus…)" sx={{ flex: 1.5 }}
                value={serviceForm.app_protocol} onChange={(e) => setServiceForm({ ...serviceForm, app_protocol: e.target.value })} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setServiceDialogOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={handleAddService} disabled={!serviceForm.name}>Добавить</Button>
        </DialogActions>
      </Dialog>

      {/* Add Flow dialog */}
      <Dialog open={flowDialogOpen} onClose={() => setFlowDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Поток — куда обращается{activeSw ? ` ${activeSw.software?.name || 'приложение'}` : ''}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Название потока (необязательно)"
              value={flowForm.name} onChange={(e) => setFlowForm({ ...flowForm, name: e.target.value })} />
            <FormControlLabel
              control={<Checkbox checked={flowForm.freeDest} onChange={(e) => setFlowForm({ ...flowForm, freeDest: e.target.checked })} />}
              label="Приёмника нет в списке (ввести вручную)" />
            {!flowForm.freeDest ? (
              <Autocomplete
                options={allServices}
                getOptionLabel={(o) => o.label || ''}
                value={allServices.find((s) => s.id === flowForm.dest_service_id) || null}
                onChange={(e, v) => setFlowForm({ ...flowForm, dest_service_id: v?.id || null })}
                renderInput={(params) => <TextField {...params} label="Приёмник (сервис)" required />}
              />
            ) : (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label="Хост / IP" sx={{ flex: 2 }}
                  value={flowForm.dest_host} onChange={(e) => setFlowForm({ ...flowForm, dest_host: e.target.value })} />
                <TextField label="Порт" type="number" sx={{ flex: 1 }}
                  value={flowForm.dest_port} onChange={(e) => setFlowForm({ ...flowForm, dest_port: e.target.value })} />
                <TextField label="L4" select sx={{ flex: 1 }}
                  value={flowForm.l4_protocol} onChange={(e) => setFlowForm({ ...flowForm, l4_protocol: e.target.value })}>
                  <MenuItem value="tcp">TCP</MenuItem>
                  <MenuItem value="udp">UDP</MenuItem>
                  <MenuItem value="icmp">ICMP</MenuItem>
                  <MenuItem value="other">Другой</MenuItem>
                </TextField>
              </Box>
            )}
            <TextField label="Назначение (telemetry/auth/db-query…)"
              value={flowForm.purpose} onChange={(e) => setFlowForm({ ...flowForm, purpose: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFlowDialogOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={handleAddFlow}
            disabled={(!flowForm.freeDest && !flowForm.dest_service_id) || (flowForm.freeDest && !flowForm.dest_port)}>
            Добавить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceSoftwareTab;
